import { getLocalStorageValue, setLocalStorageValue } from './common';
import { LocalStorageKey } from './constants/ui-constants';
import { errorLogger } from './logger';

interface ForceUpdateData {
  count: number;
  firstAttempt: number; // 첫 시도 타임스탬프
  lastAttempt: number; // 마지막 시도 타임스탬프
}

// 설정값들
const ATTEMPT_TIMEOUT = 5 * 60 * 1000; // 5분 (밀리초)
const REPEATED_ATTEMPT_THRESHOLD = 3; // 반복 시도로 판단할 임계값
const MAX_ATTEMPT_THRESHOLD = 5; // 최대 시도 횟수 (이후 모달 비활성화)

/**
 * 강제 업데이트 시도를 기록합니다
 * [동작 방식]
 * 첫 시도: localStorage에 데이터 저장
 * 2회째: 카운트만 증가
 * 3회째: 콘솔에 경고 로그 출력 + 상세 정보
 * 5분 후: 자동으로 카운트 초기화
 */
export const recordForceUpdateAttempt = async (): Promise<void> => {
  try {
    const now = Date.now();
    const existingData = getForceUpdateData();

    if (existingData && now - existingData.firstAttempt < ATTEMPT_TIMEOUT) {
      // 기존 세션 내에서의 추가 시도
      const updatedData: ForceUpdateData = {
        count: existingData.count + 1,
        firstAttempt: existingData.firstAttempt,
        lastAttempt: now,
      };
      setLocalStorageValue(LocalStorageKey.forceUpdateCount, updatedData);

      // 반복 시도 감지 시 로깅
      if (updatedData.count >= REPEATED_ATTEMPT_THRESHOLD) {
        await errorLogger(
          `[강제 업데이트 반복 감지] 사용자가 ${updatedData.count}회 시도했습니다.\n첫 시도: ${new Date(
            existingData.firstAttempt
          ).toISOString()}\n마지막 시도: ${new Date(now).toISOString()}`
        );
      }
    } else {
      // 새로운 세션 시작
      const newData: ForceUpdateData = {
        count: 1,
        firstAttempt: now,
        lastAttempt: now,
      };
      setLocalStorageValue(LocalStorageKey.forceUpdateCount, newData);
    }
  } catch (error) {
    console.error('[recordForceUpdateAttempt] 오류:', error);
  }
};

/**
 * 현재 강제 업데이트 시도 횟수를 반환합니다
 */
export const getForceUpdateCount = (): number => {
  try {
    const data = getForceUpdateData();
    if (!data) return 0;

    const now = Date.now();
    // 타임아웃을 초과한 경우 0 반환
    if (now - data.firstAttempt >= ATTEMPT_TIMEOUT) {
      clearForceUpdateCount();
      return 0;
    }

    return data.count;
  } catch (error) {
    console.error('[getForceUpdateCount] 오류:', error);
    return 0;
  }
};

/**
 * 반복 시도 상황인지 확인합니다
 */
export const isRepeatedAttempt = (): boolean => {
  return getForceUpdateCount() >= REPEATED_ATTEMPT_THRESHOLD;
};

/**
 * 최대 시도 횟수를 초과했는지 확인합니다 (5회 초과 시 true)
 */
export const hasExceededMaxAttempts = (): boolean => {
  return getForceUpdateCount() > MAX_ATTEMPT_THRESHOLD;
};

/**
 * 강제 업데이트 카운트를 초기화합니다
 */
export const clearForceUpdateCount = (): void => {
  try {
    localStorage.removeItem(LocalStorageKey.forceUpdateCount);
  } catch (error) {
    console.error('[clearForceUpdateCount] 오류:', error);
  }
};

/**
 * localStorage에서 강제 업데이트 데이터를 가져옵니다 (내부 함수)
 */
const getForceUpdateData = (): ForceUpdateData | null => {
  try {
    return getLocalStorageValue<ForceUpdateData>(LocalStorageKey.forceUpdateCount) || null;
  } catch (error) {
    console.error('[getForceUpdateData] 오류:', error);
    return null;
  }
};
